/*
 * Copyright (c) 2016-2026 Aaron Wang <aaronwqt@gmail.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import escapeHTML from "escape-html"
import {
  firstValueFrom,
  map
} from "rxjs"

import {
  requestJSON,
  watchScript
} from "~/browser"

import {
  SearchHighlighter,
  SearchMatch,
  SearchPage,
  SearchProvider,
  SearchRequest,
  SearchResponse,
  SearchResultGroup,
  SearchSnippet
} from "../../_"
import { SearchWorkerClient } from "../../worker"

import {
  LunrIndex,
  LunrIndexData,
  LunrProviderOptions
} from "./config"
import {
  LunrSearchItem,
  LunrSearchResult
} from "./engine"
import { LunrSearchHighlighter } from "./highlighter/provider"

/* ----------------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------------- */

declare global {
  // eslint-disable-next-line no-underscore-dangle
  const __index: LunrIndexData
}

/* ----------------------------------------------------------------------------
 * Helper functions
 * ------------------------------------------------------------------------- */

/* Escape source text while preserving highlights generated by Lunr */
function title(value: string): SearchSnippet {
  const html = value
    .split(/(<\/?mark>)/g)
    .map(part => /^<\/?mark>$/.test(part) ? part : escapeHTML(part))
    .join("")
  return { html }
}

/* Preserve the trusted HTML generated from the local documentation */
function excerpt(value: string): SearchSnippet {
  return { html: value }
}

/* Convert a Lunr search item into a provider-neutral match */
function match(item: LunrSearchItem): SearchMatch {
  const terms = Object.entries(item.terms)
  return {
    url: item.location,
    title: title(item.title),
    score: item.score,
    ...item.text.length && { excerpt: excerpt(item.text) },
    ...item.tags && { tags: item.tags },
    matchedTerms: terms.filter(([, value]) => value).map(([key]) => key),
    missingTerms: terms.filter(([, value]) => !value).map(([key]) => key)
  }
}

/* Convert a Lunr result group into the common result shape */
function group(items: LunrSearchItem[]): SearchResultGroup {
  const source = items.find(item => !item.location.includes("#")) || items[0]
  const page: SearchPage = {
    url: source.location,
    title: title(source.title),
    score: source.score
  }
  return {
    page,
    matches: items
      .filter(item => item !== source || Object.keys(item.terms).length > 0)
      .map(match)
  }
}

/**
 * Lunr search provider
 */
export class LunrSearchProvider implements SearchProvider {

  /* Optional result-page highlighter capability */
  public readonly highlighter: SearchHighlighter

  /* Generic worker client hosting the Lunr engine */
  private readonly worker: SearchWorkerClient<
    LunrIndex, SearchRequest, LunrSearchResult
  >

  /* Cached index and setup promises */
  private index?: Promise<LunrIndex>

  private ready?: Promise<void>

  /**
   * Create the Lunr search provider
   *
   * @param options - Lunr provider options
   */
  private readonly options: LunrProviderOptions

  public constructor(options: LunrProviderOptions) {
    this.options = {
      ...options,
      workerUrl: `${new URL(options.workerUrl, location.href)}`,
      indexUrl: `${new URL(options.indexUrl, location.href)}`,
      offlineIndexUrl: `${new URL(options.offlineIndexUrl, location.href)}`
    }
    this.worker = new SearchWorkerClient(this.options.workerUrl)
    this.highlighter = new LunrSearchHighlighter(() => this.loadIndex())
  }

  /* Load the index and initialize the Lunr worker once */
  public setup(): Promise<void> {
    if (!this.ready)
      this.ready = this.loadIndex().then(index => this.worker.setup(index))
    return this.ready
  }

  /* Search the Lunr worker and adapt its serializable result */
  public async search(request: SearchRequest): Promise<SearchResponse> {
    await this.setup()
    const response = await this.worker.search(request)
    return {
      total: response.items.length,
      results: response.items.map((items, rank) => ({
        id: items[0]?.location || `${rank}`,
        load: async () => group(items)
      })),
      ...typeof response.suggest !== "undefined" && {
        suggest: response.suggest
      }
    }
  }

  /* Load the HTTP or file:// variant of the Lunr index */
  private loadIndex(): Promise<LunrIndex> {
    if (!this.index) {
      const data = location.protocol === "file:"
        ? firstValueFrom(watchScript(this.options.offlineIndexUrl).pipe(

            map(() => __index)
          ))
        : firstValueFrom(requestJSON<LunrIndexData>(this.options.indexUrl))
      this.index = data.then(index => ({
        ...index,
        options: { suggest: this.options.suggest }
      }))
    }
    return this.index
  }
}

export * from "./config"
export * from "./engine"
